import React, { useEffect, useState } from "react";
import "./maintenanceReminders.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_MAINT_REM_PAGE_INFO,
  GET_CYCLE_CONFIRMATION_CANDIDATES,
} from "../../../../utils/GraphQL/queries";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { useNavigate } from "react-router";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrSectionPanel from "../../../../components/BdrSectionPanel/BdrSectionPanel";
import { SEND_MAINT_REM } from "../../../../utils/GraphQL/mutations";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import CycleConfirmationModal from "../../adminComponents/CycleConfirmationModal/CycleConfirmationModal";
const clearTaskObj = {
  id: "",
  assigneeName: "",
  assigneeId: "",
  taskType: "",
  note: "",
  associatedClientId: "",
  associatedClientName: "",
  active: true,
  dateCreated: "",
  dueDate: "",
};
const MaintenanceReminders = () => {
  const { handleSnack } = useSnackbar();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState("0");
  const [currentReminders, setCurrentReminders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cycleCandidates, setCycleCandidates] = useState([]);
  const [cycleFutureCount, setCycleFutureCount] = useState(0);
  const [cycleWindowDays, setCycleWindowDays] = useState(21);
  const [confirmModalTaskId, setConfirmModalTaskId] = useState(null);
  const [getPageInfo] = useLazyQuery(GET_MAINT_REM_PAGE_INFO);
  const [getCycleCandidates] = useLazyQuery(GET_CYCLE_CONFIRMATION_CANDIDATES, {
    fetchPolicy: "network-only",
  });
  const [sendMaintReminders] = useMutation(SEND_MAINT_REM);
  const [taskObj, setTaskObj] = useState({ ...clearTaskObj });
  useEffect(() => {
    window.scrollTo(0, 0);
    getPageCall();
    refreshCycleCandidates();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getPageCall = async () => {
    const { data } = await getPageInfo({
      variables: {
        currentDate: Date.now().toString(),
      },
    });
    if (data?.getMaintenanceReminderPageInfo) {
      setUpcoming(data?.getMaintenanceReminderPageInfo?.upcoming);
      setCurrentReminders([
        ...data?.getMaintenanceReminderPageInfo?.currentReminders,
      ]);
      setHistory([...data?.getMaintenanceReminderPageInfo?.history]);
    }
  };
  const refreshCycleCandidates = async () => {
    const { data } = await getCycleCandidates({
      variables: {
        currentDate: Date.now().toString(),
        withinDays: 21,
      },
    });
    const info = data?.getCycleConfirmationCandidates;
    if (info) {
      setCycleCandidates([...(info.dueSoon || [])]);
      setCycleFutureCount(info.futureCount || 0);
      setCycleWindowDays(info.windowDays || 21);
    }
  };
  const setNonModalTask = (task) => {
    setTaskObj({ ...task });
  };
  const sendMaintRemCall = async () => {
    const dateMills = Date.now();
    const offsetMills = new Date(dateMills).getTimezoneOffset() * 60 * 1000;
    const { data } = await sendMaintReminders({
      variables: {
        currentDate: dateMills?.toString(),
        offsetMills: offsetMills?.toString(),
      },
    });
    if (data?.sendMaintenanceReminders) {
      if (handleSnack) {
        handleSnack("Maintenance Reminders Sent", 3000);
      }
      getPageCall();
    }
    setLoading(false);
  };
  const hasSelection = !!taskObj?.id;

  return (
    <div className="mr-page">
      <BdrSectionPanel
        className="mr-panel"
        title="Maintenance Reminders"
        headerClassName="mr-hdr"
        bodyClassName="mr-body"
        actions={
          <BdrButton
            size="sm"
            variant="confirm"
            disabled={loading}
            onClick={() => {
              setLoading(true);
              sendMaintRemCall();
            }}
          >
            Send Reminders Due
          </BdrButton>
        }
      >
        {loading && <AdminBDRSpinner message={"Loading..."} />}

        {/* The two counts, as figures rather than a sentence inside a slab.
            They were "{n} in the next week, {m} due now" on one line of a
            gradient block that also held the send button. */}
        <div className="mr-summary">
          <div className="mr-stat">
            <span className="mr-stat-num">{upcoming}</span>
            <span className="mr-stat-label">due in the next week</span>
          </div>
          <div className="mr-stat">
            <span className="mr-stat-num">{currentReminders?.length || 0}</span>
            <span className="mr-stat-label">due now</span>
          </div>
        </div>

        {cycleCandidates.length > 0 && (
          <section className="mr-block">
            {/*
              Title, count and explanation each on their own line.

              They were an `<h3>` and a `<span>` sharing one
              `justify-content: space-between` row, with the count folded into
              the heading text in parentheses. At any real width the heading
              ran out of room and broke mid-phrase — "...21 further" / "out)"
              — while the explanation sat beside it looking like a caption for
              something else.
            */}
            <div className="mr-block-hdr">
              <h3 className="mr-block-title">Cycle Cleanup</h3>
              <span className="mr-block-count">
                {cycleCandidates.length} due in the next {cycleWindowDays} days
                {cycleFutureCount > 0 ? ` · ${cycleFutureCount} further out` : ""}
              </span>
            </div>
            <p className="mr-block-note">
              Tasks where the system couldn&apos;t determine cycle position.
              Click one to confirm it before it fires.
            </p>

            <ul className="mr-cleanup-list">
              {cycleCandidates.map((c) => (
                <li key={c.task?.id}>
                  <button
                    type="button"
                    className="mr-cleanup-item"
                    onClick={() => setConfirmModalTaskId(c.task?.id)}
                  >
                    <span className="mr-cleanup-name">
                      {c.task?.associatedClientName || "(unknown client)"}
                    </span>
                    <span className="mr-cleanup-meta">
                      {c.invoiceNum ? `Inv #${c.invoiceNum}` : "no invoice link"}
                    </span>
                    <span className="mr-cleanup-meta">
                      Due: {BAGroupTimeFormat(c.task?.dueDate, false, true)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <CycleConfirmationModal
          open={!!confirmModalTaskId}
          taskId={confirmModalTaskId}
          onClose={() => setConfirmModalTaskId(null)}
          onAfterAction={() => {
            refreshCycleCandidates();
            getPageCall();
          }}
        />

        {/* Due now and History side by side, each its own block, dropping to
            one column when they stop fitting rather than at a breakpoint. */}
        <div className="mr-cols">
          <section className="mr-block mr-col">
            <h3 className="mr-block-title">Due now</h3>

            {currentReminders?.length > 0 ? (
              <ul className="mr-list">
                {currentReminders.map((task, index) => {
                  const typeNameText = task?.associatedClientName?.length
                    ? `${task?.taskType} - ${task?.associatedClientName}`
                    : task?.taskType;
                  const selected = taskObj?.id === task?.id;

                  return (
                    <li key={`${task?.id}-${index}`}>
                      {/*
                        `mr-row--selected` is CONDITIONAL. It used to be on the
                        outer div conditionally AND hardcoded onto the inner
                        button, so every row painted the selected gradient and
                        the highlight said nothing about what was selected.
                      */}
                      <button
                        type="button"
                        aria-pressed={selected}
                        className={`mr-row${selected ? " mr-row--selected" : ""}`}
                        onClick={() => setNonModalTask(task)}
                      >
                        <span className="mr-row-top">
                          <span className="mr-row-title">{typeNameText}</span>
                          <span className="mr-row-due">
                            Due: {BAGroupTimeFormat(task?.dueDate, false, true)}
                          </span>
                        </span>
                        <span className="mr-row-note">{task?.note}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="bdr-empty">No reminders are due.</div>
            )}
          </section>

          <section className="mr-block mr-col">
            <h3 className="mr-block-title">History</h3>

            {history?.length > 0 ? (
              <ul className="mr-list mr-history">
                {history.map((item, index) => (
                  <li key={`maint-history-item-${index}`} className="mr-his-item">
                    {item?.text}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="bdr-empty">No history to display.</div>
            )}
          </section>
        </div>

        <section className="mr-block">
          <h3 className="mr-block-title">Selected task</h3>

          {/*
            Each label and its value is wrapped in a DIV inside the `<dl>`.

            That is valid HTML — a `<div>` may group a `dt`/`dd` pair inside a
            description list — and it is what makes the layout hold. As bare
            siblings, `dt` and `dd` were independent flex items: three pairs
            flowed onto the first line, each `dd` grew by its own content, and
            a label could end up on a different line from its value. Nothing
            shared a column, so nothing said what belonged to what. Grouped, a
            pair is ONE flex item and can never be split.
          */}
          {hasSelection ? (
            <dl className="mr-detail">
              <div className="mr-detail-row">
                <dt>Client</dt>
                <dd>
                  {taskObj?.associatedClientName ? (
                    <span className="mr-detail-client">
                      <span className="mr-detail-client-name">
                        {taskObj.associatedClientName}
                      </span>
                      {taskObj?.associatedClientId && (
                        <BdrButton
                          size="sm"
                          variant="action"
                          onClick={() => {
                            navigate(
                              `/bdr-office/clients/view-client/${taskObj?.associatedClientId}`
                            );
                          }}
                        >
                          View
                        </BdrButton>
                      )}
                    </span>
                  ) : (
                    <span className="mr-detail-none">No client linked</span>
                  )}
                </dd>
              </div>

              <div className="mr-detail-row">
                <dt>Task type</dt>
                <dd>{taskObj?.taskType || "—"}</dd>
              </div>

              {/* Not user-assignable — every reminder lives at assigneeId 11. */}
              <div className="mr-detail-row">
                <dt>Assigned to</dt>
                <dd>System</dd>
              </div>

              <div className="mr-detail-row">
                <dt>Due date</dt>
                <dd>{BAGroupTimeFormat(taskObj?.dueDate)}</dd>
              </div>

              <div className="mr-detail-row mr-detail-row--wide">
                <dt>Note</dt>
                {/*
                  PLAIN TEXT in a recessed box, not a read-only `BdrInput`.

                  There is no save path for this note — the page displays a
                  reminder, it does not edit one. A disabled-looking textarea
                  said "field" when the answer is "value", and it brought a
                  control height that no other row had, so the Note label sat
                  on a different line from every other label.
                */}
                <dd className="mr-detail-note">
                  {taskObj?.note || (
                    <span className="mr-detail-none">No note</span>
                  )}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="bdr-empty">
              Select a reminder above to see its details.
            </div>
          )}
        </section>
      </BdrSectionPanel>
    </div>
  );
};
export default MaintenanceReminders;
